import React, { useState, useEffect } from 'react';
import supabase from '../integrations/supabase';
import { SUPABASE_BUCKET } from '../config';

const SupabaseStorageTest = () => {
  const [testResults, setTestResults] = useState([]);
  const [loading, setLoading] = useState(false);

  const addResult = (message, type = 'info') => {
    setTestResults(prev => [...prev, { message, type, timestamp: new Date().toISOString() }]);
  };

  const runStorageTests = async () => {
    setLoading(true);
    setTestResults([]);

    try {
      // Test 1: Check if user is authenticated
      addResult('🔍 Testing user authentication...', 'info');
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        addResult(`❌ Authentication failed: ${authError?.message || 'No user found'}`, 'error');
        return;
      }
      
      addResult(`✅ User authenticated: ${user.email}`, 'success');

      // Test 2: Check bucket access
      addResult(`🔍 Testing bucket access: ${SUPABASE_BUCKET}`, 'info');
      
      try {
        const { data: bucketData, error: bucketError } = await supabase.storage.getBucket(SUPABASE_BUCKET);
        
        if (bucketError) {
          addResult(`❌ Bucket access failed: ${bucketError.message}`, 'error');
        } else {
          addResult(`✅ Bucket accessible: ${bucketData.name}`, 'success');
        }
      } catch (bucketErr) {
        addResult(`❌ Bucket test error: ${bucketErr.message}`, 'error');
      }

      // Test 3: List files in bucket
      addResult('🔍 Testing file listing...', 'info');
      
      try {
        const { data: files, error: listError } = await supabase.storage
          .from(SUPABASE_BUCKET)
          .list('', { limit: 10 });
        
        if (listError) {
          addResult(`❌ File listing failed: ${listError.message}`, 'error');
        } else {
          addResult(`✅ File listing successful. Found ${files?.length || 0} files/folders`, 'success');
          if (files && files.length > 0) {
            files.slice(0, 5).forEach(file => {
              addResult(`📁 ${file.name} (${file.metadata?.size || 'unknown size'})`, 'info');
            });
          }
        }
      } catch (listErr) {
        addResult(`❌ File listing error: ${listErr.message}`, 'error');
      }

      // Test 4: Test small file upload
      addResult('🔍 Testing small file upload...', 'info');
      
      try {
        const testContent = 'This is a test file for storage verification.';
        const testBlob = new Blob([testContent], { type: 'text/plain' });
        const testFileName = `test_${Date.now()}.txt`;
        
        const { data: uploadData, error: uploadError } = await supabase.storage
          .from(SUPABASE_BUCKET)
          .upload(`test/${testFileName}`, testBlob, {
            cacheControl: '3600',
            upsert: false
          });
        
        if (uploadError) {
          addResult(`❌ Test upload failed: ${uploadError.message}`, 'error');
        } else {
          addResult(`✅ Test upload successful: ${uploadData.path}`, 'success');
          
          // Clean up test file
          const { error: deleteError } = await supabase.storage
            .from(SUPABASE_BUCKET)
            .remove([`test/${testFileName}`]);
          
          if (deleteError) {
            addResult(`⚠️ Test file cleanup failed: ${deleteError.message}`, 'warning');
          } else {
            addResult('✅ Test file cleaned up successfully', 'success');
          }
        }
      } catch (uploadErr) {
        addResult(`❌ Test upload error: ${uploadErr.message}`, 'error');
      }

      // Test 5: Check database table
      addResult('🔍 Testing database table access...', 'info');
      
      try {
        const { data: tableData, error: tableError } = await supabase
          .from('file_embeddings')
          .select('id, file_name, subject')
          .limit(5);
        
        if (tableError) {
          addResult(`❌ Database table access failed: ${tableError.message}`, 'error');
        } else {
          addResult(`✅ Database table accessible. Found ${tableData?.length || 0} records`, 'success');
          if (tableData && tableData.length > 0) {
            tableData.forEach(record => {
              addResult(`📄 ${record.file_name} (${record.subject})`, 'info');
            });
          }
        }
      } catch (tableErr) {
        addResult(`❌ Database test error: ${tableErr.message}`, 'error');
      }

    } catch (error) {
      addResult(`❌ Test suite error: ${error.message}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <h2 className="text-2xl font-bold mb-4">Supabase Storage & Database Test</h2>
      
      <button
        onClick={runStorageTests}
        disabled={loading}
        className="mb-4 px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
      >
        {loading ? 'Running Tests...' : 'Run Storage Tests'}
      </button>

      <div className="space-y-2">
        {testResults.map((result, index) => (
          <div
            key={index}
            className={`p-3 rounded border ${
              result.type === 'error' ? 'bg-red-100 border-red-400 text-red-700' :
              result.type === 'success' ? 'bg-green-100 border-green-400 text-green-700' :
              result.type === 'warning' ? 'bg-yellow-100 border-yellow-400 text-yellow-700' :
              'bg-blue-100 border-blue-400 text-blue-700'
            }`}
          >
            <div className="flex justify-between items-start">
              <span>{result.message}</span>
              <span className="text-xs opacity-75">{new Date(result.timestamp).toLocaleTimeString()}</span>
            </div>
          </div>
        ))}
      </div>

      {testResults.length === 0 && !loading && (
        <div className="text-gray-500 text-center py-8">
          Click "Run Storage Tests" to check your Supabase configuration
        </div>
      )}
    </div>
  );
};

export default SupabaseStorageTest; 